import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Rocket, ShieldCheck, Clock, Zap } from "lucide-react";
import { toast } from "sonner";
import { createLead, TANGMANGO_URL } from "@/lib/api";
import { trackLead } from "@/lib/tracking";
import { COUNTRIES, DEFAULT_COUNTRY_CODE } from "@/lib/countries";

const DEFAULT_PLAN = "Free 5-Day Workshop";

export default function LeadForm({ formRef }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    countryCode: DEFAULT_COUNTRY_CODE,
  });
  const [phoneError, setPhoneError] = useState("");
  const [loading, setLoading] = useState(false);

  const selectedCountry = useMemo(
    () => COUNTRIES.find((c) => c.code === form.countryCode) || COUNTRIES[0],
    [form.countryCode]
  );

  const update = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const handlePhoneChange = (e) => {
    // strip everything except digits
    const digitsOnly = (e.target.value || "").replace(/\D/g, "");
    // enforce max length of selected country
    const trimmed = digitsOnly.slice(0, selectedCountry.length);
    setForm((prev) => ({ ...prev, phone: trimmed }));
    if (phoneError && trimmed.length === selectedCountry.length) setPhoneError("");
  };

  const handleCountryChange = (newCode) => {
    const next = COUNTRIES.find((c) => c.code === newCode) || COUNTRIES[0];
    // trim phone to new country's max length
    setForm((prev) => ({
      ...prev,
      countryCode: newCode,
      phone: prev.phone.slice(0, next.length),
    }));
    setPhoneError("");
  };

  const validatePhone = () => {
    if (!form.phone) {
      setPhoneError("WhatsApp number is required");
      return false;
    }
    if (form.phone.length !== selectedCountry.length) {
      setPhoneError(
        `Please enter a valid ${selectedCountry.length}-digit WhatsApp number.`
      );
      return false;
    }
    setPhoneError("");
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please fill all fields to continue");
      return;
    }
    if (!validatePhone()) {
      toast.error(`Please enter a valid ${selectedCountry.length}-digit WhatsApp number.`);
      return;
    }

    const fullPhone = `${selectedCountry.dial}${form.phone}`;
    setLoading(true);
    try {
      await createLead({
        name: form.name,
        email: form.email,
        phone: fullPhone,
        fees: DEFAULT_PLAN,
      });
      trackLead({ value: 0, currency: "INR", plan: DEFAULT_PLAN });

      toast.success("Seat reserved! Redirecting you to checkout…");
      setTimeout(() => {
        const url = `${TANGMANGO_URL}?name=${encodeURIComponent(
          form.name
        )}&email=${encodeURIComponent(form.email)}&phone=${encodeURIComponent(fullPhone)}`;
        window.location.href = url;
      }, 900);
    } catch (err) {
      console.error("Form submission error:", err);
      console.error("Error response:", err.response?.data);
      console.error("Error message:", err.message);
      console.error("Error status:", err.response?.status);
      const errorMsg = err.response?.data?.detail || err.message || "Something went wrong";
      toast.error(`Error: ${errorMsg}. Please try again or WhatsApp us.`);
      setLoading(false);
    }
  };

  return (
    <section
      id="register"
      ref={formRef}
      className="relative py-20 md:py-28 px-6 md:px-10 overflow-hidden"
      data-testid="lead-form-section"
    >
      <div className="absolute inset-0">
        <img
          src="https://images.pexels.com/photos/7562087/pexels-photo-7562087.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt=""
          className="w-full h-full object-cover opacity-10"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628] via-[#0A1628]/90 to-[#0A1628]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-[#38BDF8]/5 blur-[140px]" />
      </div>

      <div className="relative max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        {/* Copy side */}
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#38BDF8]/10 border border-[#38BDF8]/30 text-xs font-semibold text-[#38BDF8]">
            <Zap className="w-3.5 h-3.5" />
            Batch Starts This Sunday
          </div>
          <h2 className="font-display text-4xl md:text-5xl font-normal text-white mt-5 leading-tight">
            One form.
            <br />
            A <span className="text-[#38BDF8]">different career</span> by March.
          </h2>
          <p className="mt-6 text-slate-400 leading-relaxed text-lg">
            Fill in your details, reserve your seat, and you'll be redirected to
            secure TagMango checkout.
          </p>

          <ul className="mt-8 space-y-3">
            {[
              { icon: ShieldCheck, text: "100% guarantee to get GenAI skills as return on your investment." },
              { icon: Clock, text: "AI is changing the industry rapidly. It’s time to evolve with it." },
              { icon: Rocket, text: "Secure your career and accelerate your growth with GenAI Automation skills." },
            ].map((b, i) => (
              <li key={i} className="flex items-start gap-3" data-testid={`form-benefit-${i}`}>
                <b.icon className="w-5 h-5 text-[#38BDF8] mt-0.5 flex-shrink-0" />
                <span className="text-slate-300">{b.text}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Form side */}
        <form
          onSubmit={handleSubmit}
          noValidate
          className="bg-slate-900/80 backdrop-blur-xl border border-slate-800 rounded-3xl p-7 md:p-10 space-y-5"
          data-testid="lead-capture-form"
        >
          <div className="flex items-center justify-between">
            <h3 className="font-display text-2xl font-normal text-white">
              Reserve My Seat
            </h3>
            <span className="text-xs text-[#38BDF8] font-semibold">
              15 seats left
            </span>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name" className="text-slate-300 text-xs uppercase tracking-widest">
              Full Name
            </Label>
            <Input
              id="name"
              data-testid="form-input-name"
              placeholder="Priya Sharma"
              value={form.name}
              onChange={update("name")}
              className="bg-[#0A1628] border-slate-800 h-12 text-white focus:border-[#38BDF8] focus:ring-[#38BDF8]/30"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-slate-300 text-xs uppercase tracking-widest">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              data-testid="form-input-email"
              placeholder="you@example.com"
              value={form.email}
              onChange={update("email")}
              className="bg-[#0A1628] border-slate-800 h-12 text-white focus:border-[#38BDF8] focus:ring-[#38BDF8]/30"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone" className="text-slate-300 text-xs uppercase tracking-widest">
              WhatsApp Number
            </Label>
            <div className="flex gap-2">
              <Select value={form.countryCode} onValueChange={handleCountryChange}>
                <SelectTrigger
                  className="w-[130px] sm:w-[150px] bg-[#0A1628] border-slate-800 h-12 text-white focus:ring-[#38BDF8]/30"
                  data-testid="form-select-country"
                  aria-label="Country code"
                >
                  <SelectValue>
                    <span className="flex items-center gap-2">
                      <span className="text-base leading-none">{selectedCountry.flag}</span>
                      <span className="text-sm font-medium">{selectedCountry.dial}</span>
                    </span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent
                  className="bg-slate-900 border-slate-800 text-white max-h-72"
                  data-testid="country-dropdown-content"
                >
                  {COUNTRIES.map((c) => (
                    <SelectItem
                      key={c.code}
                      value={c.code}
                      data-testid={`country-option-${c.code}`}
                      className="focus:bg-[#38BDF8]/10 focus:text-white"
                    >
                      <span className="flex items-center gap-2.5">
                        <span className="text-base leading-none">{c.flag}</span>
                        <span className="text-slate-400 text-xs w-12 tabular-nums">
                          {c.dial}
                        </span>
                        <span className="text-sm">{c.name}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Input
                id="phone"
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="tel-national"
                data-testid="form-input-phone"
                placeholder={"•".repeat(selectedCountry.length)}
                value={form.phone}
                onChange={handlePhoneChange}
                onBlur={() => form.phone && validatePhone()}
                maxLength={selectedCountry.length}
                aria-invalid={!!phoneError}
                aria-describedby={phoneError ? "phone-error" : undefined}
                className={`flex-1 bg-[#0A1628] h-12 text-white focus:ring-[#38BDF8]/30 ${
                  phoneError
                    ? "border-red-500 focus:border-red-500"
                    : "border-slate-800 focus:border-[#38BDF8]"
                }`}
              />
            </div>
            {phoneError ? (
              <p
                id="phone-error"
                className="text-xs text-red-400 mt-1"
                data-testid="phone-error-message"
              >
                {phoneError}
              </p>
            ) : (
              <p className="text-[11px] text-slate-500">
                Enter {selectedCountry.length} digits. Numbers only.
              </p>
            )}
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="btn-yellow w-full h-14 rounded-full font-bold uppercase tracking-wider text-sm"
            data-testid="form-submit-button"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Reserving…
              </>
            ) : (
              <>
                Reserve My Seat @ ₹99/- 
                <Rocket className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>

          <p className="text-xs text-slate-500 text-center">
            By submitting, you'll be redirected to secure checkout. Your details
            are saved only for workshop enrollment.
          </p>
        </form>
      </div>
    </section>
  );
}
